import React from 'react';
import './MarketOverviewDark.css';

const MarketOverviewDark = ({ data }) => {
  if (!data) return null;

  const stats = {
    totalScanned: data.total_stocks_scanned || 0,
    smallCapGappers: data.top_movers?.small_cap_gappers?.length || 0,
    largeCapGappers: data.top_movers?.large_cap_gappers?.length || 0,
    gapDowns: data.top_movers?.gap_downs?.length || 0,
    lowFloat: data.top_movers?.low_float_runners?.length || 0,
    pennyStocks: data.top_movers?.penny_stock_movers?.length || 0
  };

  // Calculate gap distribution
  const calculateGapDistribution = () => {
    if (!data.top_movers) return [];

    const allStocks = Object.values(data.top_movers).flat();
    const distribution = {
      'negative': 0,
      '0-2%': 0,
      '2-5%': 0,
      '5-10%': 0,
      '10-20%': 0,
      '>20%': 0
    };

    allStocks.forEach(stock => {
      const gap = stock.gap_pct || 0;
      if (gap < 0) distribution['negative']++;
      else if (gap <= 2) distribution['0-2%']++;
      else if (gap <= 5) distribution['2-5%']++;
      else if (gap <= 10) distribution['5-10%']++;
      else if (gap <= 20) distribution['10-20%']++;
      else distribution['>20%']++;
    });

    return Object.entries(distribution).map(([range, count]) => ({
      range,
      count,
      percentage: allStocks.length > 0 ? (count / allStocks.length * 100) : 0
    }));
  };

  const gapDistribution = calculateGapDistribution();
  const maxGapCount = Math.max(...gapDistribution.map(d => d.count), 1);

  // Calculate category percentages for pie chart
  const total = stats.smallCapGappers + stats.largeCapGappers + stats.gapDowns + stats.pennyStocks;
  const categoryPercentages = [
    { name: 'Small Caps', value: stats.smallCapGappers, percentage: (stats.smallCapGappers / total * 100).toFixed(1) },
    { name: 'Large Caps', value: stats.largeCapGappers, percentage: (stats.largeCapGappers / total * 100).toFixed(1) },
    { name: 'Gap Downs', value: stats.gapDowns, percentage: (stats.gapDowns / total * 100).toFixed(1) },
    { name: 'Penny Stocks', value: stats.pennyStocks, percentage: (stats.pennyStocks / total * 100).toFixed(1) }
  ];

  return (
    <div className="market-overview-dark">
      <h2 className="overview-title">Market Overview</h2>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-number">{stats.totalScanned.toLocaleString()}</div>
          <div className="stat-label">Stocks Scanned</div>
        </div>
        <div className="stat-card green">
          <div className="stat-number">{stats.smallCapGappers}</div>
          <div className="stat-label">Small Cap Gappers</div>
        </div>
        <div className="stat-card blue">
          <div className="stat-number">{stats.largeCapGappers}</div>
          <div className="stat-label">Large Cap Gappers</div>
        </div>
        <div className="stat-card red">
          <div className="stat-number">{stats.gapDowns}</div>
          <div className="stat-label">Gap Downs</div>
        </div>
        <div className="stat-card purple">
          <div className="stat-number">{stats.pennyStocks}</div>
          <div className="stat-label">Penny Movers</div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="charts-row">
        {/* Gap Distribution Chart */}
        <div className="chart-card">
          <h3>Gap % Distribution</h3>
          <div className="bar-chart">
            {gapDistribution.map((item, index) => (
              <div key={index} className="bar-item">
                <div className="bar-label">{item.range}</div>
                <div className="bar-container">
                  <div
                    className={`bar ${item.range === 'negative' ? 'negative' : ''}`}
                    style={{ width: `${(item.count / maxGapCount) * 100}%` }}
                  >
                    <span className="bar-value">{item.count}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Category Distribution Pie Chart */}
        <div className="chart-card">
          <h3>Category Distribution</h3>
          <div className="pie-chart-container">
            <svg viewBox="0 0 200 200" className="pie-chart">
              {(() => {
                let cumulativePercentage = 0;
                const radius = 80;
                const centerX = 100;
                const centerY = 100;

                return categoryPercentages.map((category, index) => {
                  const startAngle = (cumulativePercentage * 360) / 100;
                  const endAngle = ((cumulativePercentage + parseFloat(category.percentage)) * 360) / 100;
                  cumulativePercentage += parseFloat(category.percentage);

                  const startAngleRad = (startAngle * Math.PI) / 180;
                  const endAngleRad = (endAngle * Math.PI) / 180;

                  const x1 = centerX + radius * Math.cos(startAngleRad);
                  const y1 = centerY + radius * Math.sin(startAngleRad);
                  const x2 = centerX + radius * Math.cos(endAngleRad);
                  const y2 = centerY + radius * Math.sin(endAngleRad);

                  const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

                  const pathData = [
                    `M ${centerX} ${centerY}`,
                    `L ${x1} ${y1}`,
                    `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                    'Z'
                  ].join(' ');

                  const colors = ['#10b981', '#3b82f6', '#ef4444', '#a855f7'];

                  return (
                    <path
                      key={index}
                      d={pathData}
                      fill={colors[index]}
                      stroke="#1a1a1f"
                      strokeWidth="2"
                      className="pie-slice"
                    />
                  );
                });
              })()}
            </svg>
            <div className="pie-legend">
              {categoryPercentages.map((category, index) => {
                const colors = ['#10b981', '#3b82f6', '#ef4444', '#a855f7'];
                return (
                  <div key={index} className="legend-item">
                    <span
                      className="legend-color"
                      style={{ backgroundColor: colors[index] }}
                    ></span>
                    <span className="legend-label">
                      {category.name}: {category.value} ({category.percentage}%)
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketOverviewDark;